import { useEffect, useState } from "react";
import { ArrowRight, Terminal } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1565164370954-8eac883fb7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxkYXJrJTIwY2luZW1hdGljJTIwc3B5JTIwdGhyaWxsZXIlMjBkb3NzaWVyfGVufDB8fHx8MTc4Mjg4NzQ5OXww&ixlib=rb-4.1.0&q=85";

const lines = [
  "> establishing secure channel...",
  "> handshake ok · dossier LUNIPIN-01",
  "> loading behavioural engine...",
  "> access granted // proceed",
];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % lines.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end pt-24 pb-16 overflow-hidden"
    >
      {/* Background image + overlays */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/75 to-[#050505]" />
      <div className="absolute inset-0 scanlines opacity-70" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(220,38,38,0.15),transparent_50%)]" />

      {/* Top-left dossier tag */}
      <div className="absolute top-32 md:top-28 left-6 lg:left-10 z-10 font-mono text-[10px] text-neutral-400 uppercase tracking-[0.3em] flex items-center gap-3">
        <span className="text-[#dc2626]">●</span> Classified · Dossier 01
        <span className="hidden md:inline text-neutral-600">/ Access · Executive</span>
      </div>

      {/* Top-right timestamp */}
      <div className="absolute top-32 md:top-28 right-6 lg:right-10 z-10 font-mono text-[10px] text-neutral-500 uppercase tracking-[0.25em] hidden md:block">
        LAT 12.9716°N · LON 77.5946°E <span className="text-[#f59e0b]">// LIVE</span>
      </div>

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-9">
            <div className="font-mono text-xs uppercase tracking-[0.4em] text-[#dc2626] mb-6 flex items-center gap-3">
              <Terminal size={14} />
              A Workplace Intelligence Simulation
            </div>
            <h1
              data-testid="hero-title"
              className="font-display uppercase font-black leading-[0.88] tracking-[-0.02em] text-[clamp(3rem,10vw,9rem)]"
            >
              The game
              <br />
              <span className="text-neutral-500">already</span>{" "}
              <span className="relative inline-block">
                exists
                <span className="text-[#dc2626]">.</span>
              </span>
            </h1>

            <p className="mt-8 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed">
              We design high-stakes, story-driven simulations that turn corporate offsites,
              L&D programs and recognition events into{" "}
              <span className="text-white font-semibold">live behavioural experiments</span> —
              not icebreakers. Real storyline. Real pressure. A debrief leadership can
              actually use.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="#products"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-3 bg-[#dc2626] hover:bg-[#991b1b] px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] font-semibold transition-all hover:translate-y-[-2px] hover:shadow-[0_12px_30px_rgba(220,38,38,0.5)]"
              >
                Enter The Hunt
                <ArrowRight size={16} />
              </a>
              <a
                href="#engine"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/20 hover:border-white px-8 py-4 font-mono text-xs uppercase tracking-[0.25em] font-semibold transition-colors"
              >
                See The Engine
              </a>
            </div>
          </div>

          {/* Terminal panel */}
          <div className="col-span-12 lg:col-span-3 self-end">
            <div className="terminal-frame p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <span className="w-2.5 h-2.5 bg-[#dc2626]" />
                <span className="w-2.5 h-2.5 bg-[#f59e0b]" />
                <span className="w-2.5 h-2.5 bg-neutral-500" />
                <span className="ml-auto font-mono text-[10px] text-neutral-500">
                  lunipin.sh
                </span>
              </div>
              <div className="font-mono text-[11px] leading-relaxed">
                {lines.slice(0, idx + 1).map((l, i) => (
                  <div
                    key={i}
                    className={i === idx ? "text-[#f59e0b] cursor-blink" : "text-neutral-500"}
                  >
                    {l}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stat strip */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/10">
          {[
            ["06", "Shield Championships"],
            ["04", "Story Networks"],
            ["05", "Recognition Dimensions"],
            ["30d", "Pilot Duration"],
          ].map(([n, l], i) => (
            <div
              key={i}
              className="bg-[#050505] p-5 md:p-6"
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-display text-3xl md:text-5xl font-black">{n}</div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                {l}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
